// lib/internal-traffic.ts
//
// Durable, per-device opt-out that marks the current browser as team/internal
// traffic so the team browsing the live production site never pollutes the
// production analytics property. This complements the dev-hostname guard in
// app/analytics/injectGA4.ts, which only covers localhost — it does nothing
// about staff QA'ing or clicking their own CTAs on eclatinstitute.sg itself.
//
// GA4's own internal-traffic exclusion (IP-based rule + a report Data Filter)
// is UI-only and fragile against dynamic home/mobile IPs, so we tag at the
// point of collection instead, where we own the code:
//
//   - Set the flag once per browser by visiting the live site with
//     ?ga_internal=1 (clear it with ?ga_internal=0). The value persists in
//     localStorage, so it survives across sessions and IP changes.
//   - injectGA4 skips loading GA4 entirely for a flagged browser, so those hits
//     never reach the GA4 property.
//   - trackJourneyEvent stamps traffic_type="internal" on the first-party JSONL
//     record (which is still written) so the same rows can be filtered out in
//     BigQuery / downstream analysis.
//
// Browser-only APIs (window / localStorage / URLSearchParams). No Node builtins,
// so this stays safe to import from "use client" modules.

const INTERNAL_TRAFFIC_STORAGE_KEY = "eclat_internal_traffic";
const INTERNAL_TRAFFIC_QUERY_PARAM = "ga_internal";
const AUTOMATION_SOURCE_STORAGE_KEY = "eclat_automation_source";
const AUTOMATION_SOURCE_QUERY_PARAM = "ga_automation";
const AUDIT_RUN_STORAGE_KEY = "eclat_audit_run_id";
const AUDIT_RUN_QUERY_PARAM = "ga_audit_run";

const PROVENANCE_VALUE_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._-]{0,63}$/;

/**
 * The GA4-canonical value for the internal-traffic parameter. Stamped on the
 * first-party record for flagged browsers; also the value a GA4 Data Filter
 * would key on if one is ever created in the UI.
 */
export const INTERNAL_TRAFFIC_TYPE = "internal";

export type InternalTrafficContext = {
  trafficType?: typeof INTERNAL_TRAFFIC_TYPE;
  automationSource?: string;
  auditRunId?: string;
};

function normalizeProvenanceValue(value: string | null): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  return PROVENANCE_VALUE_PATTERN.test(trimmed) ? trimmed : null;
}

function readStoredFlag(): boolean {
  try {
    return window.localStorage.getItem(INTERNAL_TRAFFIC_STORAGE_KEY) === "1";
  } catch {
    // Storage disabled (private mode, blocked cookies): treat as external.
    return false;
  }
}

/**
 * Apply a ?ga_internal=1 / ?ga_internal=0 toggle from the current URL to the
 * durable localStorage flag, if the param is present. Safe to call repeatedly;
 * absent param leaves the stored value untouched.
 */
function syncFlagFromQuery(): void {
  try {
    const params = new URLSearchParams(window.location.search);
    const value = params.get(INTERNAL_TRAFFIC_QUERY_PARAM);
    const automationSource = normalizeProvenanceValue(
      params.get(AUTOMATION_SOURCE_QUERY_PARAM),
    );
    const auditRunId = normalizeProvenanceValue(
      params.get(AUDIT_RUN_QUERY_PARAM),
    );

    // A provenance marker is itself an explicit declaration that the visit is
    // an owned audit. This keeps browser automation out of GA4 even if a caller
    // forgets to include the separate ga_internal toggle.
    if (automationSource || auditRunId) {
      window.localStorage.setItem(INTERNAL_TRAFFIC_STORAGE_KEY, "1");
    }
    if (automationSource) {
      window.sessionStorage.setItem(
        AUTOMATION_SOURCE_STORAGE_KEY,
        automationSource,
      );
    }
    if (auditRunId) {
      window.sessionStorage.setItem(AUDIT_RUN_STORAGE_KEY, auditRunId);
    }

    if (value === null) return;
    if (value === "0" || value === "false") {
      window.localStorage.removeItem(INTERNAL_TRAFFIC_STORAGE_KEY);
      window.sessionStorage.removeItem(AUTOMATION_SOURCE_STORAGE_KEY);
      window.sessionStorage.removeItem(AUDIT_RUN_STORAGE_KEY);
    } else {
      window.localStorage.setItem(INTERNAL_TRAFFIC_STORAGE_KEY, "1");
    }
  } catch {
    // Storage disabled: nothing to persist, fall through to external.
  }
}

/**
 * True when this browser has been flagged as internal/team traffic. Reads the
 * durable flag, first applying any ?ga_internal toggle in the current URL so a
 * single visit both sets the flag and takes effect on the same page load.
 * SSR-safe: returns false when there is no window.
 */
export function isInternalTraffic(): boolean {
  if (typeof window === "undefined") return false;
  syncFlagFromQuery();
  return readStoredFlag();
}

/**
 * Return bounded provenance for first-party telemetry. These values are never
 * sent to GA4 because owned audit traffic is excluded before GA4 loads.
 */
export function getInternalTrafficContext(): InternalTrafficContext {
  if (typeof window === "undefined") return {};
  syncFlagFromQuery();
  if (!readStoredFlag()) return {};

  try {
    const automationSource = normalizeProvenanceValue(
      window.sessionStorage.getItem(AUTOMATION_SOURCE_STORAGE_KEY),
    );
    const auditRunId = normalizeProvenanceValue(
      window.sessionStorage.getItem(AUDIT_RUN_STORAGE_KEY),
    );

    return {
      trafficType: INTERNAL_TRAFFIC_TYPE,
      ...(automationSource ? { automationSource } : {}),
      ...(auditRunId ? { auditRunId } : {}),
    };
  } catch {
    return { trafficType: INTERNAL_TRAFFIC_TYPE };
  }
}
